import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { buildLocaleLoadPath } from './loadPath';
import { i18nPerformance } from './performance';
import { readRuntimeAppBuildId } from '../utils/runtimeConfig';

// Supported languages configuration
export const SUPPORTED_LANGUAGES = {
  'zh-CN': {
    code: 'zh-CN',
    name: 'Simplified Chinese',
    nativeName: '简体中文',
    direction: 'ltr' as const,
    dateFormat: 'YYYY-MM-DD',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
  },
  'zh-HK': {
    code: 'zh-HK',
    name: 'Traditional Chinese',
    nativeName: '繁體中文',
    direction: 'ltr' as const,
    dateFormat: 'YYYY-MM-DD',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
  },
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    direction: 'ltr' as const,
    dateFormat: 'YYYY-MM-DD',
    numberFormat: {
      decimal: '.',
      thousands: ',',
    },
  },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// Translation namespaces
export const TRANSLATION_NAMESPACES = [
  'common',
  'auth',
  'detection',
  'statistics',
  'profile',
  'navigation',
  'errors',
  'history',
  'home',
  'device',
  'admin',
  'payment',
] as const;

export type TranslationNamespace = typeof TRANSLATION_NAMESPACES[number];

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';
const localeCacheVersion = readRuntimeAppBuildId() || __APP_BUILD_ID__;

const getFirstBackendValue = (
  value: string | readonly string[] | undefined,
  fallbackValue: string
): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value[0] ?? fallbackValue;
  }

  return fallbackValue;
};

// Initialize i18next with simplified configuration
const initI18n = async () => {
  // Read language from localStorage before initialization
  const savedLanguage = localStorage.getItem('i18nextLng');
  const initialLanguage =
    savedLanguage &&
    (savedLanguage === 'zh-CN' || savedLanguage === 'zh-HK' || savedLanguage === 'en')
      ? savedLanguage
      : 'zh-CN';

  console.log('[i18n] Initializing with language:', initialLanguage, 'from localStorage:', savedLanguage);

  await i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // Language settings - use saved language as initial
      lng: initialLanguage,
      load: 'currentOnly',
      fallbackLng: 'zh-CN',
      supportedLngs: ['zh-CN', 'zh-HK', 'en'],

      // Namespace settings
      ns: TRANSLATION_NAMESPACES,
      defaultNS: 'common',

      // Detection settings - prioritize localStorage
      detection: {
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: 'i18nextLng',
        // Add language mapping
        convertDetectedLanguage: (lng: string) => {
          // Handle browser language code mapping
          console.log('[i18n] Detected language:', lng);

          // Handle various Chinese variants
          if (lng === 'zh' || lng === 'zh-CN' || lng.startsWith('zh-CN') || lng === 'zh-Hans') {
            return 'zh-CN';
          }
          if (
            lng === 'zh-HK' ||
            lng === 'zh-TW' ||
            lng.startsWith('zh-HK') ||
            lng.startsWith('zh-TW') ||
            lng === 'zh-Hant'
          ) {
            return 'zh-HK';
          }
          if (lng === 'en' || lng.startsWith('en')) {
            return 'en';
          }

          // Default to Simplified Chinese
          console.log('[i18n] Using fallback language: zh-CN for detected language:', lng);
          return 'zh-CN';
        },
      },

      // Interpolation settings
      interpolation: {
        escapeValue: false, // React already escapes values
      },

      // Backend settings for loading translations
      backend: {
        loadPath: (languages: readonly string[], namespaces: readonly string[]) =>
          buildLocaleLoadPath(
            getFirstBackendValue(languages, initialLanguage),
            getFirstBackendValue(namespaces, 'common'),
            localeCacheVersion
          ),
        requestOptions: {
          cache: 'default',
          credentials: 'same-origin',
          mode: 'cors',
        },
        // Add language mapping for resource loading
        customHeaders: {},
        parse: (data: string) => JSON.parse(data),
        // Handle language fallback for resource loading
        allowMultiLoading: false,
      },

      // React settings
      react: {
        useSuspense: false,
      },

      // Debug settings (only in development)
      debug: isDevelopment,

      // Key separator
      keySeparator: '.',
      nsSeparator: ':',

      // Return objects for nested translations
      returnObjects: true,

      // Return null for missing keys to show fallback
      returnNull: false,
      returnEmptyString: false,

      // Pluralization
      pluralSeparator: '_',
      contextSeparator: '_',
    });
};

// Initialize i18n
initI18n().catch(console.error);

export default i18n;

// Export performance instance
export { i18nPerformance };
